import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import gqlMacro from 'graphql-tag.macro';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useInterval, useTimeout } from 'react-use';

import type { FiatCurrency } from '__generated__/globalTypes';
import { LoadingIndicator } from 'atoms/loader/LoadingIndicator';
import { RedirectAfterSignIn } from 'contexts/currentUser/RedirectAfterSignIn';
import { useGraphqlContext } from 'contexts/graphql';
import { coreCache } from 'contexts/graphql/Provider';
import { useSportContext } from 'contexts/sport';
import { useTMContext } from 'contexts/tm';
import { useQuery } from 'hooks/graphql/useQuery';
import { useQueryLoader } from 'hooks/graphql/useQueryLoader';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { usePageVisibility } from 'hooks/usePageVisibility';
import {
  getMinorRate,
  monetaryAmountFragment,
  ratesFragment,
} from 'lib/monetaryAmount';
import { queryLoader } from 'lib/navigation/queryLoader';

import type { AlgoliaCardIndexes, AlgoliaIndexes } from '.';
import { ConfigContext } from '.';
import { currentUser } from '../currentUser/queries';
import type {
  ConfigProvider_TeamsPlayingNextGameWeek,
  ConfigQueryVariables,
  PingConfigQuery,
  PingConfigQueryVariables,
  ReportTelemetry,
  ReportTelemetryVariables,
  ScoutableSo5Competitions_query,
} from './__generated__/Provider.graphql';
import type { ConfigQuery, ConfigQuery_currentUser } from './types';

const teamPlayingNextGameWeekFragment = gqlMacro`
  fragment ConfigProvider_TeamsPlayingNextGameWeek on TeamsPlayingNextGameWeek {
    slug
    gameWeek
    shortDisplayName
    longDisplayName
    endDate
    startDate
    teamSlugs
  }
` as TypedDocumentNode<ConfigProvider_TeamsPlayingNextGameWeek>;

const scoutableSo5CompetitionsFragment = gql`
  fragment ScoutableSo5Competitions_query on Query {
    so5 {
      id
      currentManagerHasLiveGames(sport: $sport)
      cardMaxGrade(sport: $sport)
      inSeasonName: seasonalityName(seasonality: IN_SEASON, sport: $sport)
      allSeasonsName: seasonalityName(seasonality: ALL_SEASONS, sport: $sport)
      myAvailableFixtureStory {
        id
        aasmState
      }
      so5Competitions(sport: $sport, eventType: CLASSIC) {
        slug
        displayName
        logoUrl
        iconUrl
        backgroundUrl
        algoliaFilters
        scoutable
        competitions {
          slug
          displayName
          pictureUrl
        }
      }
    }
  }
` as TypedDocumentNode<ScoutableSo5Competitions_query>;

const CONFIG_QUERY = gqlMacro`
  query ConfigQuery($sport: Sport!, $withCollectionPredictions: Boolean = true) {
    config {
      id
      algoliaIndexSuffix
      algoliaApplicationId
      algoliaSearchApiKey
      baseBankAddress
      ethereumBankDepositAddress
      baseCrossDomainDepositAddress
      l1MessengerAddress
      starkExchangeAddress
      ethereumGatewayMinterProxyAddress
      ethereumNetworkId
      ethereumEndpoint
      solanaBankAuthority
      sorareEncryptionKey
      minimumReceiveWeiAmount
      marketFeeRateBasisPoints
      idealDepositFeesAmountMinor
      mangopayAmexFeesBasisPoints: creditCardFeesBasisPoints(
        creditCardType: AMEX
        provider: MANGOPAY
      )
      mangopayVisaFeesBasisPoints: creditCardFeesBasisPoints(
        creditCardType: CB_VISA_MASTERCARD
        provider: MANGOPAY
      )
      paypalDepositFeesBasisPoints
      mangopayApplePayDepositFeesBasisPoints
      stripeCreditCardFeesBasisPoints: creditCardFeesBasisPoints(
        provider: STRIPE
      )
      paypalMinimumBidAmount: minimumBidAmount(provider: PAYPAL) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      exchangeRate {
        id
        ethRates { ...RatesFragment_rates }
        time
      }
      defaultFiatCurrency
      ethQuantum
      so5 {
        id
        teamsPlayingNextDailyGameWeeks: teamsPlayingNextGameWeeks(
          eventType: DAILY
          sport: $sport
        ) {
          slug
          ...ConfigProvider_TeamsPlayingNextGameWeek
        }
        teamsPlayingNextClassicGameWeeks: teamsPlayingNextGameWeeks(
          eventType: CLASSIC
          sport: $sport
          first: 5
        ) {
          slug
          ...ConfigProvider_TeamsPlayingNextGameWeek
        }
        dailyTasksExpiresAt: tasksExpiresAt(periodicity: DAILY)
      }
      currentLocation {
        countryCode
        regionCode
      }
      counts {
        usersCount
      }
      activeRevealableCardCampaigns {
        slug
        name
        environmentPictureUrl
        blueprintCardEditions
        myBlueprintCards(sport: $sport) {
          totalCount
        }
      }
    }
    currentUser {
      slug
      ...CurrentUserProvider_currentUser
    }
    tokens {
      promotionalEvents: promotionalEvents(sport: $sport) {
        slug
        name
        title
        blogUrl
        description
        logoUrl
        pictureUrl
        startDate
        endDate
        algoliaFilters
        ... on MarketPromotionalEvent {
          slug
          hasInstantBuy
        }
        ... on SpecialEditionPromotionalEvent {
          slug
          hasInstantBuy
        }
        ... on CardShardThresholdsPromotionalEvent {
          slug
          eligibilityRules{
            id
            eligibleRarities
          }
        }
      }
    }
    market {
      id
      tasks(sport: $sport) {
        id
        aasmState
      }
      wheelTasks: tasks(sport: $sport, genre: DAILY_WHEEL_TICKET) {
        id
        aasmState
      }
    }
    ...ScoutableSo5Competitions_query
  }
  ${currentUser}
  ${teamPlayingNextGameWeekFragment}
  ${scoutableSo5CompetitionsFragment}
  ${ratesFragment}
  ${monetaryAmountFragment}
` as TypedDocumentNode<ConfigQuery, ConfigQueryVariables>;

const PING_QUERY = gqlMacro`
  query PingConfigQuery($sport: Sport!) {
    config {
      id
      exchangeRate {
        id
        ethRates { ...RatesFragment_rates }
        time
      }
      minimumReceiveWeiAmount
    }
    so5 {
      currentManagerHasLiveGames(sport: $sport)
    }
    currentUser {
      slug
    }
  }
  ${ratesFragment}
` as TypedDocumentNode<PingConfigQuery, PingConfigQueryVariables>;

const TM_MUTATION = gqlMacro`
  mutation ReportTelemetry($input: reportTelemetryInput!) {
    reportTelemetry(input: $input) {
      errors {
        message
      }
    }
  }
` as TypedDocumentNode<ReportTelemetry, ReportTelemetryVariables>;

interface Props {
  children: ReactNode;
}

const getRandomInt = (max: number) => {
  return Math.floor(Math.random() * max);
};

const pingInterval = 115_000 + getRandomInt(10_000); // 2Min +-5 sec;
const backgroundPingInterval = 1_795_000 + getRandomInt(10_000); //30 Min +- 5sec;
const tmInterval = 30_000 + getRandomInt(5_000);

export const loader = queryLoader(CONFIG_QUERY, ({ sport }) => ({ sport }));

/**
 * Provides environment dependent configuration keys.
 * Aggregates all startup loading states so that initialization can be done in parallel
 */
export const ConfigProvider = ({ children }: Props) => {
  const { data, refetch, loading } = useQueryLoader<ConfigQuery>();
  const { sport } = useSportContext();
  const [isPingQueryReady] = useTimeout(pingInterval);

  const isVisible = usePageVisibility();

  useQuery(PING_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
    pollInterval: isVisible ? pingInterval : backgroundPingInterval,
    skip: !isPingQueryReady(),
    variables: { sport },
  });
  const {
    flags: { useReportTelemetry },
  } = useFeatureFlags();
  const { refreshWsCable } = useGraphqlContext();
  const flushOperations = useTMContext()?.flushOperations;
  const [tm] = useMutation(TM_MUTATION, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  });

  const {
    config: { algoliaIndexSuffix },
  } = data;

  const { algoliaCardIndexes, algoliaIndexes } = useMemo(() => {
    const aci: AlgoliaCardIndexes = {
      // blockchain cards indices
      New: `BlockchainCard_New${algoliaIndexSuffix}`,
      'Ending Soon': `BlockchainCard_EndingSoon${algoliaIndexSuffix}`,
      'Newly Listed': `BlockchainCard_NewlyListed${algoliaIndexSuffix}`,
      'Highest Average Score': `BlockchainCard_HighestAverageScore${algoliaIndexSuffix}`,
      'Highest Price': `BlockchainCard${algoliaIndexSuffix}`,
      'Lowest Price': `BlockchainCard_LowestPrice${algoliaIndexSuffix}`,
      'Best Value': `BlockchainCard_BestValue${algoliaIndexSuffix}`,
      'Popular Player': `BlockchainCard_Popularity${algoliaIndexSuffix}`,

      'Cards On Sale Ending Soon': `CardsOnSale_EndingSoon${algoliaIndexSuffix}`,
      'Cards On Sale Newly Listed': `CardsOnSale_NewlyListed${algoliaIndexSuffix}`,
      'Cards On Sale Highest Average Score': `CardsOnSale_HighestAverageScore${algoliaIndexSuffix}`,
      'Cards On Sale Highest Price': `CardsOnSale${algoliaIndexSuffix}`,
      'Cards On Sale Lowest Price': `CardsOnSale_LowestPrice${algoliaIndexSuffix}`,
      'Cards On Sale Best Value': `CardsOnSale_BestValue${algoliaIndexSuffix}`,
      'Cards On Sale Popular Player': `CardsOnSale_Popularity${algoliaIndexSuffix}`,
      'Cards On Sale Bids Count': `CardsOnSale_BidsCount${algoliaIndexSuffix}`,
    };

    const ai: AlgoliaIndexes = {
      ...aci,
      User: `User${algoliaIndexSuffix}`,
      Player: `Player${algoliaIndexSuffix}`,
      Club: `Club${algoliaIndexSuffix}`,
      League: `League${algoliaIndexSuffix}`,
      Country: `Country${algoliaIndexSuffix}`,
      'National Team': `NationalTeam${algoliaIndexSuffix}`,
      Competition: `Competition${algoliaIndexSuffix}`,
    };
    return {
      algoliaIndexes: ai,
      algoliaCardIndexes: aci,
    };
  }, [algoliaIndexSuffix]);

  const value = useMemo(() => {
    const { config, tokens, so5, market } = data;
    const { exchangeRate, minimumReceiveWeiAmount } = config;

    return {
      ...config,
      ...tokens,
      ...market,
      so5: {
        ...config.so5,
        ...so5,
      },
      wheel: {
        tasks: market.wheelTasks,
      },
      marketFeeRateBasisPoints: config.marketFeeRateBasisPoints / 10000,
      marketFeePercentagePoints: BigInt(config.marketFeeRateBasisPoints) / 100n,
      minimumReceiveWeiAmount,
      algoliaIndexes,
      algoliaCardIndexes,
      currentUser: data.currentUser,
      exchangeRate: {
        ...exchangeRate,
        getEthRate: (to: FiatCurrency) =>
          getMinorRate(exchangeRate.ethRates, to).div(100).toNumber(),
      },
      refetch,
      loading,
      updateQuery: (newCurrentUser: ConfigQuery_currentUser) => {
        coreCache.updateQuery(
          {
            query: CONFIG_QUERY,
          },
          () => ({ ...data, currentUser: newCurrentUser })
        );
        refreshWsCable();
      },
      defaultFiatCurrency: config.defaultFiatCurrency,
    };
  }, [
    data,
    refetch,
    loading,
    refreshWsCable,
    algoliaCardIndexes,
    algoliaIndexes,
  ]);

  useInterval(
    () => {
      if (flushOperations) {
        const operations = flushOperations();
        if (!operations.length) {
          return;
        }
        tm({ variables: { input: { operations } } });
      }
    },
    useReportTelemetry ? tmInterval : null
  );

  if (!value) return <LoadingIndicator fullScreen />;

  return (
    <RedirectAfterSignIn currentUser={value.currentUser}>
      <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
    </RedirectAfterSignIn>
  );
};
